import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  CircularProgress,
  Paper,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Button,
  IconButton,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import swal from 'sweetalert';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';

import { useEntity } from '../../context/EntityContext';
import {
  getAllInformationPublications,
  deleteInformationPublication
} from '../../services/informationPublicationsService';

function formatDate(dateString?: string): string {
  if (!dateString) return '—';
  try {
    const date = new Date(dateString);
    return new Intl.DateTimeFormat('pt-BR', {
      dateStyle: 'long',
      timeStyle: 'short'
    }).format(date);
  } catch {
    return dateString;
  }
}

function formatOnlyDate(dateString?: string): string {
  if (!dateString) return '—';
  try {
    // Adiciona T00:00:00 se a string for apenas data para evitar problemas de fuso
    const date = new Date(dateString.includes('T') ? dateString : `${dateString}T00:00:00`);
    return new Intl.DateTimeFormat('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    }).format(date);
  } catch {
    return dateString;
  }
}

const AllInformationPublications: React.FC = () => {
  const navigate = useNavigate();
  const { selectedEntity } = useEntity();
  const [loading, setLoading] = useState(false);
  const [publications, setPublications] = useState<any[]>([]);

  const fetchPublications = async () => {
    if (!selectedEntity) return;
    setLoading(true);
    try {
      const data = await getAllInformationPublications(selectedEntity);
      setPublications(data);
    } catch (err) {
      console.error('Erro ao obter publicações de informação:', err);
      swal('Erro', 'Não foi possível carregar as informações.', 'error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (selectedEntity) {
      fetchPublications();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedEntity]);

  const handleRowClick = (info: any) => {
    navigate(`/informacoes/${info.id}`);
  };

  const handleDelete = async (info: any, e: React.MouseEvent) => {
    e.stopPropagation(); // para não disparar o handleRowClick
    const confirmed = await swal({
      title: 'Tem certeza?',
      text: `Deseja excluir a informação "${info.title || 'Sem título'}"?`,
      icon: 'warning',
      buttons: ['Cancelar', 'Excluir'],
      dangerMode: true,
    });
    if (!confirmed) return;

    try {
      await deleteInformationPublication(info.id);
      swal('Sucesso', 'Excluído com sucesso.', 'success');
      fetchPublications();
    } catch (error) {
      console.error('Erro ao excluir:', error);
      swal('Erro', 'Não foi possível excluir a informação.', 'error');
    }
  };

  if (!selectedEntity) {
    return (
      <Box sx={{ mt: 4 }}>
        <Typography variant="body1" color="error">
          Selecione uma entidade antes de prosseguir.
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ mt: 4 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
        <Typography variant="h5">Informações</Typography>
        <Button variant="contained" onClick={() => navigate('/informacoes/nova')}>
          Nova Informação
        </Button>
      </Box>

      {loading ? (
        <Box sx={{ textAlign: 'center', mt: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          {publications.length === 0 ? (
            <Typography>Nenhuma informação cadastrada.</Typography>
          ) : (
            <Paper sx={{ p: 2 }}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Título</TableCell>
                    <TableCell>Autor</TableCell>
                    <TableCell>Data do Documento</TableCell>
                    <TableCell>Criado em</TableCell>
                    <TableCell>Resumo</TableCell>
                    <TableCell align="center">Ações</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {publications.map((info) => (
                    <TableRow
                      key={info.id}
                      hover
                      onClick={() => handleRowClick(info)}
                      sx={{ cursor: 'pointer' }}
                    >
                      <TableCell>{info.title || 'Sem título'}</TableCell>
                      <TableCell>{info.author || '—'}</TableCell>
                      <TableCell>{formatOnlyDate(info.document_date)}</TableCell>
                      <TableCell>{formatDate(info.created_at)}</TableCell>
                      <TableCell>
                        {info.summary?.slice(0, 100) || 'Sem resumo'}
                        {info.summary && info.summary.length > 100 && '...'}
                      </TableCell>
                      <TableCell align="center" onClick={(e) => e.stopPropagation()}>
                        <IconButton
                          color="error"
                          size="small"
                          onClick={(e) => handleDelete(info, e)}
                        >
                          <DeleteForeverIcon fontSize="small" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Paper>
          )}
        </>
      )}
    </Box>
  );
};

export default AllInformationPublications;